import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types for Contract DTOs
export interface ContractDto {
  id: number;
  leadId: number;
  contractNumber: string;
  status: string;
  commissionPercentage: number;
  commissionAmount?: number;
  sentAt?: string;
  viewedAt?: string;
  signedAt?: string;
  expiresAt?: string;
  documentSetId?: string;
  hasAccessCode: boolean;
  accessCodeLastFour?: string;
  createdAt: string;
  leadName: string;
  createdByUserName: string;
  documents: ContractDocumentDto[];
}

export interface ContractDocumentDto {
  id: number;
  documentType: string;
  documentUrl: string;
  status: string;
  signedAt?: string;
  hasSignature: boolean;
  manuallyUploaded: boolean;
  manualUploadUrl?: string;
}

/**
 * DTO לפתיחת תהליך חתימה.
 * פרטי החתימה האישיים נשמרים מראש דרך PUT /api/leads/{id}/signing-details —
 * כאן רק שדות יצירת החוזה (אימייל / קוד גישה / עמלה).
 */
export interface InitiateSigningDto {
  leadId: number;
  email: string;
  commissionPercentage: number;
  accessCode: string;
  // אישור הנציג שהכתובת תואמת לכתובת המעודכנת במשרד הפנים — נדרש להמשך תהליך החתימה
  addressConfirmed: boolean;
}

export interface UpdateAccessCodeDto {
  accessCode: string;
}

export interface VerifyAccessCodeDto {
  accessCode: string;
}

export interface SigningSessionDto {
  documentSetId: string;
  clientName: string;
  clientIdNumber: string;
  commissionPercentage: number;
  documents: SigningDocumentDto[];
  expiresAt: string;
}

export interface SigningDocumentDto {
  id: number;
  documentType: string;
  title: string;
  htmlContent: string;
  requiresSignature: boolean;
  isSigned: boolean;
  requiresDualSignature: boolean; // האם נדרשות 2 חתימות (אב ואם) - לחוזה ילדים
}

export interface SubmitSignatureDto {
  documentId: number;
  signatureData: string; // Base64 encoded signature image (חתימת אב בחוזה ילדים)
  motherSignatureData?: string; // Base64 encoded signature image - חתימת האם (לחוזה ילדים בלבד)
}

export interface SubmitSignaturesDto {
  signatures: SubmitSignatureDto[];
  acceptTerms: boolean;
  clientIdNumber: string; // תעודת זהות
}

export interface SigningResultDto {
  success: boolean;
  message: string;
  clientId?: number;
}

export interface AccessCodeVerificationResult {
  token: string;
  message: string;
}

// DTO מפושט להעלאה ידנית של חוזים חתומים
// בהעלאה ידנית המסמכים כבר מלאים וחתומים - אין צורך בשדות תבנית
export interface ManualUploadSigningDto {
  leadId: number;
  commissionPercentage: number;
  clientIdNumber: string;
  // הערה: אימייל ושאר הפרטים נלקחים מה-Lead אוטומטית
}

// קבצים להעלאה ידנית
export interface ManualUploadFiles {
  nationalInsuranceAuth: File;
  serviceAgreement: File;
}

// ============================================================================
// DTOs - מסמכי חוזה עבור תיקיית לקוח
// ============================================================================

/** מסמך חוזה בודד עבור תצוגה בתיקיית לקוח */
export interface ContractDocumentForClientDto {
  id: number;
  documentType: string;
  documentTypeName: string; // שם בעברית
  status: string;
  isSigned: boolean;
  signedAt?: string;
  hasSignature: boolean;
  signatureData?: string; // Base64
  hasMotherSignature: boolean;
  motherSignatureData?: string; // Base64
  motherSignedAt?: string;
  manuallyUploaded: boolean;
  manualUploadUrl?: string; // URL for manually uploaded files
  createdAt: string;
}

/** מסמכי חוזה של לקוח - עבור תיקיית "מסמכי חוזה" בטאב מסמכים */
export interface ClientContractDocumentsDto {
  clientId: number;
  contractId?: number;
  contractNumber: string;
  contractSignedAt?: string;
  documents: ContractDocumentForClientDto[];
  hasContract: boolean;
}

/** תוצאת המרת תמונה ל-PDF */
export interface ImageConversionResultDto {
  success: boolean;
  documentId: number;
  newFileName: string;
  newFileExtension: string;
  newFileSizeBytes: number;
  errorMessage?: string;
}

// Contract API service
export const contractApi = {
  /**
   * Initiate signing process for a lead
   */
  initiateSigning: async (data: InitiateSigningDto): Promise<ContractDto> => {
    const response = await axios.post(`${API_BASE_URL}/contracts/initiate-signing`, data);
    return response.data;
  },

  /**
   * Verify access code for signing session (No auth required)
   */
  verifyAccessCode: async (documentSetId: string, data: VerifyAccessCodeDto): Promise<AccessCodeVerificationResult> => {
    const response = await axios.post(`${API_BASE_URL}/contracts/verify-access/${documentSetId}`, data);
    return response.data;
  },

  /**
   * Get signing session documents (Requires signing token)
   */
  getSigningSession: async (documentSetId: string, signingToken: string): Promise<SigningSessionDto> => {
    const response = await axios.get(`${API_BASE_URL}/contracts/signing/${documentSetId}`, {
      headers: {
        'Authorization': `Bearer ${signingToken}`
      }
    });
    return response.data;
  },

  /**
   * Submit signatures (Requires signing token)
   */
  submitSignatures: async (documentSetId: string, data: SubmitSignaturesDto, signingToken: string): Promise<SigningResultDto> => {
    const response = await axios.post(`${API_BASE_URL}/contracts/sign/${documentSetId}`, data, {
      headers: {
        'Authorization': `Bearer ${signingToken}`
      }
    });
    return response.data;
  },

  /**
   * Update access code for existing contract
   */
  updateAccessCode: async (contractId: number, data: UpdateAccessCodeDto): Promise<ContractDto> => {
    const response = await axios.put(`${API_BASE_URL}/contracts/${contractId}/access-code`, data);
    return response.data;
  },

  /**
   * Get contract by lead ID
   */
  getByLeadId: async (leadId: number): Promise<ContractDto | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/by-lead/${leadId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get contract by ID
   */
  getById: async (contractId: number): Promise<ContractDto | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/${contractId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Download signed document PDF
   */
  downloadDocumentPdf: async (documentId: number): Promise<Blob> => {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/contracts/document/${documentId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * העלאה ידנית של חוזים חתומים (PDF files)
   */
  manualUploadSigning: async (
    data: ManualUploadSigningDto,
    files: ManualUploadFiles
  ): Promise<ContractDto> => {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(data));
    formData.append('nationalInsuranceAuthFile', files.nationalInsuranceAuth);
    formData.append('serviceAgreementFile', files.serviceAgreement);

    const response = await axios.post(`${API_BASE_URL}/contracts/manual-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * קבלת מסמכי חוזה של לקוח - עבור תיקיית "מסמכי חוזה" בטאב מסמכים
   */
  getContractDocumentsByClientId: async (clientId: number): Promise<ClientContractDocumentsDto> => {
    const response = await axios.get<ClientContractDocumentsDto>(
      `${API_BASE_URL}/contracts/client/${clientId}/documents`
    );
    return response.data;
  },

  /**
   * קבלת HTML של מסמך לצפייה - מחזיר את התוכן המלא כולל חתימות
   */
  getDocumentHtml: async (documentId: number): Promise<string> => {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/contracts/document/${documentId}/html`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'text'
    });
    return response.data;
  },

  /**
   * המרת מסמך תמונה ל-PDF (רק למסמכים שהועלו ידנית)
   */
  convertDocumentToPdf: async (documentId: number): Promise<ImageConversionResultDto> => {
    const response = await axios.post<ImageConversionResultDto>(
      `${API_BASE_URL}/contracts/document/${documentId}/convert-to-pdf`
    );
    return response.data;
  }
};

// Utility functions
export const getContractStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'draft':
      return '#9ca3af'; // Gray
    case 'sent':
      return '#3b82f6'; // Blue
    case 'viewed':
      return '#f59e0b'; // Yellow
    case 'partialsigned':
      return '#ef4444'; // Red
    case 'fullysigned':
      return '#10b981'; // Green
    case 'expired':
      return '#6b7280'; // Dark gray
    case 'cancelled':
      return '#ef4444'; // Red
    default:
      return '#9ca3af';
  }
};

export const getContractStatusText = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'draft':
      return 'טיוטה';
    case 'sent':
      return 'נשלח';
    case 'viewed':
      return 'נצפה';
    case 'partialsigned':
      return 'חתום חלקית';
    case 'fullysigned':
      return 'חתום מלא';
    case 'expired':
      return 'פג תוקף';
    case 'cancelled':
      return 'בוטל';
    default:
      return status;
  }
};

export const getDocumentTypeText = (documentType: string): string => {
  switch (documentType.toLowerCase()) {
    case 'serviceagreement':
    case 'serviceagreementgeneral':
      return 'הסכם שירות';
    case 'serviceagreementchildren':
      return 'הסכם שירות - ילדים';
    case 'serviceagreementcare':
      return 'הסכם שירות - סיעוד';
    case 'medicalwaiver':
      return 'ויתור סודיות רפואית (ישן)';
    case 'nationalinsuranceauth':
      return 'ייפוי כוח - ויתור סודיות';
    default:
      return documentType;
  }
};

// Form validation helpers
export const validateAccessCode = (accessCode: string): boolean => {
  return accessCode.length >= 4 && accessCode.length <= 20;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const generateRandomAccessCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Israeli ID number validation - re-exported from validationUtils
export { validateIsraeliId } from '../utils/validationUtils';